import React, { useEffect, useRef, useState } from "react";
import { Palette, Upload, Loader2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, fileUrl } from "@/lib/apiClient";
import { toast } from "sonner";

export const BrandKitDialog = () => {
  const [open, setOpen] = useState(false);
  const [bk, setBk] = useState(null);
  const [fonts, setFonts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const logoRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/brand-kit").then(({ data }) => { setBk(data.brand_kit); setFonts(data.fonts); }).catch(() => {});
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/brand-kit", { primary: bk.primary, secondary: bk.secondary, font: bk.font });
      setBk(data.brand_kit);
      toast.success("Brand kit saved");
      setOpen(false);
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/brand-kit/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBk(data.brand_kit);
      toast.success("Logo uploaded");
    } catch { toast.error("Logo upload failed"); } finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-full border-white/15 bg-transparent hover:bg-white/5 text-white" data-testid="brand-kit-btn">
          <Palette className="w-4 h-4 mr-1" /> Brand Kit
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-zinc-950 border-white/10 text-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Brand Kit</DialogTitle>
          <DialogDescription className="text-white/50">Your colors, font and logo are applied to graphics, subtitles and thumbnails.</DialogDescription>
        </DialogHeader>
        {!bk ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#00E5FF]" /></div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white/70 text-sm">Primary color</Label>
                <div className="flex items-center gap-2 mt-1.5">
                  <input type="color" value={bk.primary} onChange={(e) => setBk({ ...bk, primary: e.target.value })}
                    className="w-10 h-10 rounded-md bg-black border border-white/15 cursor-pointer" data-testid="brand-primary-input" />
                  <span className="text-sm text-white/60">{bk.primary}</span>
                </div>
              </div>
              <div>
                <Label className="text-white/70 text-sm">Secondary color</Label>
                <div className="flex items-center gap-2 mt-1.5">
                  <input type="color" value={bk.secondary} onChange={(e) => setBk({ ...bk, secondary: e.target.value })}
                    className="w-10 h-10 rounded-md bg-black border border-white/15 cursor-pointer" data-testid="brand-secondary-input" />
                  <span className="text-sm text-white/60">{bk.secondary}</span>
                </div>
              </div>
            </div>
            <div>
              <Label className="text-white/70 text-sm">Subtitle font</Label>
              <Select value={bk.font} onValueChange={(v) => setBk({ ...bk, font: v })}>
                <SelectTrigger className="mt-1.5 bg-black border-white/15" data-testid="brand-font-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-white/10 text-white">
                  {fonts.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/70 text-sm">Logo (stamped on thumbnails)</Label>
              <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="brand-logo-input" />
              <div className="flex items-center gap-3 mt-1.5">
                <Button variant="outline" onClick={() => logoRef.current?.click()} disabled={uploading}
                  className="border-white/15 bg-transparent hover:bg-white/5 text-white" data-testid="brand-logo-upload-btn">
                  {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading…</> : <><Upload className="w-4 h-4 mr-1" />Upload logo</>}
                </Button>
                {bk.logo_path && (
                  <div className="flex items-center gap-2">
                    <img src={fileUrl(bk.logo_path)} alt="logo" className="w-9 h-9 object-contain rounded bg-white/5 border border-white/10" />
                    <Check className="w-4 h-4 text-[#39FF14]" />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button onClick={save} disabled={saving || !bk}
            className="w-full rounded-full bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 font-medium" data-testid="brand-save-btn">
            {saving ? "Saving…" : "Save brand kit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
